import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { TrailPool } from './TrailPool';
import { Game } from './Game';

export class HardEnemy extends GameObject {
  private static readonly SIZE = 32;
  private static readonly FILL = new Color(245, 195, 68);
  private static readonly GLOW = new Color(245, 195, 68, 35);
  private static readonly TRAIL_COLOR = new Color(245, 195, 68);

  private handler: Handler;
  private r: Random = new Random();
  private trailTick = 0;
  private rotation = 0;
  private targetRotation = 0;
  private readonly boundsRect = new Rectangle();
  private hp = -1;

  constructor(x: number, y: number, id: ID, handler: Handler) {
    super(x, y, id);
    this.handler = handler;
    this.velX = 5;
    this.velY = 5;
    this.targetRotation = Math.atan2(this.velY, this.velX);
    this.rotation = this.targetRotation;
  }

  setCombatHp(hp: number): void { this.hp = hp; }

  override takeDamage(dmg: number): boolean {
    if (this.hp < 0) return false;
    this.hp -= dmg;
    if (this.hp <= 0) { this.die(); return true; }
    return true;
  }

  private die(): void {
    const half = Math.trunc(HardEnemy.SIZE / 2);
    for (let i = 0; i < 6; i++) {
      const angle = Math.random() * Math.PI * 2;
      TrailPool.add(
        this.x + half + Math.cos(angle) * 8,
        this.y + half + Math.sin(angle) * 8,
        HardEnemy.FILL, half, half, 0.05, TrailPool.SHAPE_TRIANGLE);
    }
    Game.addEnemyKilled();
    Game.addKillBonus(100);
    this.handler.removeObject(this);
  }

  getBounds(): Rectangle {
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = HardEnemy.SIZE;
    this.boundsRect.height = HardEnemy.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    const half = Math.trunc(HardEnemy.SIZE / 2);
    this.x += this.velX;
    this.y += this.velY;

    // Rotate toward movement direction with snap on bounce
    this.targetRotation = Math.atan2(this.velY, this.velX);
    let diff = this.targetRotation - this.rotation;
    // Normalize angle difference
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.rotation += diff * 0.15;

    if (this.y <= 0 || this.y >= Game.HEIGHT - HardEnemy.SIZE) {
      Game.wallHit(this.x + half, this.y <= 0 ? 0 : Game.HEIGHT, this.y <= 0 ? 0 : 1, 245, 195, 68);
      this.velY = (this.y <= 0) ? (this.r.nextInt(7) + 1) : -(this.r.nextInt(7) + 1);
      this.targetRotation = Math.atan2(this.velY, this.velX);
    }
    if (this.x <= 0 || this.x >= Game.WIDTH - HardEnemy.SIZE) {
      Game.wallHit(this.x <= 0 ? 0 : Game.WIDTH, this.y + half, this.x <= 0 ? 2 : 3, 245, 195, 68);
      this.velX = (this.x <= 0) ? (this.r.nextInt(7) + 1) : -(this.r.nextInt(7) + 1);
      this.targetRotation = Math.atan2(this.velY, this.velX);
    }
    if (++this.trailTick % 3 === 0)
      TrailPool.add(this.x, this.y, HardEnemy.TRAIL_COLOR, HardEnemy.SIZE, HardEnemy.SIZE, 0.02, TrailPool.SHAPE_TRIANGLE);
  }

  render(g: Graphics2D): void {
    const g2 = g;
    const cx = Math.trunc(this.x) + Math.trunc(HardEnemy.SIZE / 2);
    const cy = Math.trunc(this.y) + Math.trunc(HardEnemy.SIZE / 2);
    const half = Math.trunc(HardEnemy.SIZE / 2);

    // Triangle pointing in movement direction
    g2.setColor(HardEnemy.GLOW);
    this.drawRotatedTriangle(g2, cx, cy, half + 4, this.rotation);
    g2.setColor(HardEnemy.FILL);
    this.drawRotatedTriangle(g2, cx, cy, half, this.rotation);
  }

  private drawRotatedTriangle(g: Graphics2D, cx: number, cy: number, r: number, angle: number): void {
    const xp: number[] = new Array<number>(3).fill(0);
    const yp: number[] = new Array<number>(3).fill(0);
    for (let i = 0; i < 3; i++) {
      const a = angle + i * (Math.PI * 2 / 3);
      xp[i] = cx + Math.trunc(Math.cos(a) * r);
      yp[i] = cy + Math.trunc(Math.sin(a) * r);
    }
    g.fillPolygon(xp, yp, 3);
  }
}
